import { keys } from 'ramda';
import type { Editor, NodeEntry } from 'slate';
import { Transforms } from 'slate';

import type {
  FooterElement,
  FootersElement,
  HeaderElement,
  HeadersElement,
  RenderModelFooter,
  RenderModelHeader,
  RMDocProps,
} from '../internal';
import {
  createDefaultFooterByType,
  createDefaultHeaderByType,
  FooterType,
  HeaderType,
  isFootersElement,
  isHeadersElement,
  Queries,
} from '../internal';

export type HeadersOrFootersElement<T extends 'header' | 'footer'> =
  | ('header' extends T ? HeadersElement : never)
  | ('footer' extends T ? FootersElement : never);

export type HeaderOrFooterElement<T extends 'header' | 'footer'> =
  | ('header' extends T ? HeaderElement : never)
  | ('footer' extends T ? FooterElement : never);

export type HeaderOrFooterType<T extends 'header' | 'footer'> =
  | ('header' extends T ? HeaderType : never)
  | ('footer' extends T ? FooterType : never);

export type RenderModelHeaderOrFooterElement<T extends 'header' | 'footer'> =
  | ('header' extends T ? RenderModelHeader : never)
  | ('footer' extends T ? RenderModelFooter : never);

export const headersAndFootersQueries = {
  /**
   * Get the active render model footer on a given page.
   */
  activeFooterOnPage: (args: { pageNumber: number; rMDocProps: RMDocProps }): RenderModelFooter => {
    const { pageNumber, rMDocProps } = args;

    return rMDocProps.footers.children[pageNumber - 1];
  },

  activeFooterTypes: (editor: Editor): FooterType[] => {
    const [document] = Queries.getDocument(editor);
    const activeHeaderFooterProps = Queries.activeHeaderOrFooterProps(document);

    return [
      FooterType.Default,
      ...keys(activeHeaderFooterProps).flatMap(key => {
        switch (key) {
          case 'diffFirstPage': {
            const firstPageActive = activeHeaderFooterProps[key];
            return firstPageActive ? [FooterType.FirstPage] : [];
          }
          case 'evenOdd': {
            const evenOddActive = activeHeaderFooterProps[key];
            return evenOddActive ? [FooterType.Even] : [];
          }
          default: {
            return [];
          }
        }
      }),
    ];
  },

  /**
   * Get the active render model header on a given page.
   */
  activeHeaderOnPage: (args: { pageNumber: number; rMDocProps: RMDocProps }): RenderModelHeader => {
    const { pageNumber, rMDocProps } = args;

    return rMDocProps.headers.children[pageNumber - 1];
  },

  /**
   * Returns the active header and footer types, taking into account suggested edits.
   */
  activeHeaderOrFooterTypes: (editor: Editor): (FooterType | HeaderType)[] => {
    const activeFooterTypes = Queries.activeFooterTypes(editor);
    const activeHeaderTypes = Queries.activeHeaderTypes(editor);

    return [...activeFooterTypes, ...activeHeaderTypes];
  },

  activeHeaderTypes: (editor: Editor): HeaderType[] => {
    const [document] = Queries.getDocument(editor);
    const activeHeaderFooterProps = Queries.activeHeaderOrFooterProps(document);

    return [
      HeaderType.Default,
      ...keys(activeHeaderFooterProps).flatMap(key => {
        switch (key) {
          case 'diffFirstPage': {
            const firstPageActive = activeHeaderFooterProps[key];
            return firstPageActive ? [HeaderType.FirstPage] : [];
          }
          case 'evenOdd': {
            const evenOddActive = activeHeaderFooterProps[key];
            return evenOddActive ? [HeaderType.Even] : [];
          }
          default: {
            return [];
          }
        }
      }),
    ];
  },

  /**
   * Return the footers element.
   */
  getFootersElement: (editor: Editor): NodeEntry<FootersElement> | void => {
    const footersIndex = editor.children.findIndex(isFootersElement);
    if (footersIndex === -1) return;

    const footersElement = editor.children[footersIndex] as FootersElement;

    return [footersElement, [footersIndex]];
  },

  /**
   * Get a particular header or footer by type within the headers or footers element.
   */
  getHeaderOrFooter<T extends 'header' | 'footer'>(options: {
    editor: Editor;
    partEntry: NodeEntry<HeadersOrFootersElement<T>>;
    type?: HeaderOrFooterType<T>;
  }): NodeEntry<HeaderOrFooterElement<T>> {
    const { editor, partEntry } = options;
    const [part, partPath] = partEntry;
    const isHeader = isHeadersElement(part);

    const defaultType = (
      isHeader ? HeaderType.Default : FooterType.Default
    ) as HeaderOrFooterType<T>;
    const { type = defaultType } = options;

    const elementIndex = part.children.findIndex(element => element.type === type);
    if (elementIndex === -1) {
      const element = (
        isHeader
          ? createDefaultHeaderByType(type as HeaderType)
          : createDefaultFooterByType(type as FooterType)
      ) as HeaderOrFooterElement<T>;

      const elementPath = partPath.concat(part.children.length);
      Transforms.insertNodes(editor, element, { at: elementPath });
      return [element, elementPath];
    }

    const element = part.children[elementIndex] as HeaderOrFooterElement<T>;
    const elementPath = partPath.concat(elementIndex);
    return [element, elementPath];
  },

  /**
   * Return the headers element.
   */
  getHeadersElement: (editor: Editor): NodeEntry<HeadersElement> | void => {
    const headersIndex = editor.children.findIndex(isHeadersElement);
    if (headersIndex === -1) return;

    const headers = editor.children[headersIndex] as HeadersElement;

    return [headers, [headersIndex]];
  },
};
